import { useState } from "react";
import { ScheduleStopSettlement } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { Account } from "@common/types/domains/accounting/account";
import { ScheduleOptionsModal } from "../options/ScheduleOptionsModal";
import { ScheduleStopOptions } from "./ScheduleStopOptions";
import {
    ScheduleStopChoice,
    ScheduleStopSummary,
    getScheduleStopConfirmLabel,
    resolveScheduleStopSettlement,
} from "./scheduleStopSettlementCopy";

export interface ScheduleStopModalContent {
    title: string;
    policyLine?: string;
    body: string;
    note?: string;
    summary: ScheduleStopSummary;
    /** Null when a bulk selection spans entities: the account picker is entity-scoped. */
    entityId: number | null;
}

interface ScheduleStopModalProps {
    show: boolean;
    content: ScheduleStopModalContent | null;
    isSubmitting: boolean;
    onConfirm: (
        settlement: ScheduleStopSettlement | undefined,
    ) => Promise<void>;
    onHide: () => void;
    onExited: () => void;
}

/**
 * Stop and what to do with the remaining balance are one decision: the
 * choice rides on the stop request, so nothing posts until it is confirmed.
 */
export const ScheduleStopModal = ({
    show,
    content,
    isSubmitting,
    onConfirm,
    onHide,
    onExited,
}: ScheduleStopModalProps) => {
    const [choice, setChoice] = useState<ScheduleStopChoice | null>(null);
    const [reclassAccount, setReclassAccount] = useState<Account | null>(null);
    const resolved = resolveScheduleStopSettlement({
        choice,
        reclassAccountId: reclassAccount?.id ?? null,
    });

    const handleExited = () => {
        setChoice(null);
        setReclassAccount(null);
        onExited();
    };

    return (
        <ScheduleOptionsModal
            show={show}
            content={
                content
                    ? {
                          title: content.title,
                          policyLine: content.policyLine,
                          body: content.body,
                          note: content.note,
                          options: (
                              <ScheduleStopOptions
                                  summary={content.summary}
                                  entityId={content.entityId}
                                  choice={choice}
                                  onChoiceChange={setChoice}
                                  reclassAccount={reclassAccount}
                                  onReclassAccountChange={setReclassAccount}
                              />
                          ),
                          confirmLabel: getScheduleStopConfirmLabel(choice),
                          isConfirmDisabled: resolved === null,
                      }
                    : null
            }
            isSubmitting={isSubmitting}
            onConfirm={() =>
                resolved ? onConfirm(resolved.settlement) : undefined
            }
            onHide={onHide}
            onExited={handleExited}
        />
    );
};
